
import React from "react";
import { Schemes } from "../../utils";
import { i18nManager } from "../../i18n";
import { AlertEvent, AlertInterface, Alignment, ButtonManageRef, LoadingAlertDialog, MessageSchemesIcons, NoseurElement, NoseurIconElement, Scheme, Toaster, alertDialog, loadingAlertDialog, MessageProps, Position } from "@rivtn/noseur";

export interface ConfirmProps {
    desc: string;
    title: string;
    closable: boolean;
    maximized: boolean;
    noControl: boolean;
    cancelLabel: string;
    cancelScheme: Scheme;
    confirmScheme: Scheme;
    forceClosable: boolean;
    content: NoseurElement;
    confirmTextOnly: boolean;
    dismissibleModal: boolean;
    style: React.CSSProperties;
    confirmLabel: string | null;
    rightContent: NoseurElement;
    footerAlignment?: Alignment;
    footerElements: NoseurElement;
    centerIcon: NoseurIconElement;
    rightContentIsDynamic: boolean;
    reverseControlActions: boolean;
    rightStyle: React.CSSProperties;
    confirmStyle: React.CSSProperties;
    contentStyle: React.CSSProperties;
    component?: <T>(props: Partial<LoadingAlertDialog<any, T>>, params?: T | undefined) => AlertInterface;

    onCancel: AlertEvent,
    onConfirm: (confirmButton?: ButtonManageRef) => (Promise<boolean> | boolean),
    onLoading: <T>(alert: AlertInterface, params?: T) => Promise<boolean | undefined>
}

export function ConfirmDialog(props: Partial<ConfirmProps>): AlertInterface & { update: (props: Partial<ConfirmProps>) => void; } {
    const confirmButton: any = { current: undefined };

    const alertComponent = props.component ?? (!props.onLoading ? alertDialog : loadingAlertDialog);
    if (props.closable && !props.cancelLabel) props.cancelLabel = i18nManager.Labels.common.cancel;
    const cancel = (!props.noControl || props.forceClosable) && props.cancelLabel ? {
        action: props.onCancel,
        label: props.cancelLabel,
        scheme: props.cancelScheme ?? Schemes.OMNEHOST,
        props: { textOnly: true, style: { fontWeight: "bold", marginRight: 20 } },
    } : null;
    const footerAlignment = props.footerAlignment ?? /*(cancel ? */Alignment.RIGHT/* : Alignment.CENTER)*/;
    const confirm = props.noControl ? null : {
        scheme: props.confirmScheme ?? Schemes.OMNEHOST,
        label: props.confirmLabel ?? i18nManager.Labels.common.continue,
        props: {
            fill: !cancel,
            manageRef: confirmButton,
            raised: !props.confirmTextOnly,
            textOnly: props.confirmTextOnly,
            style: { ...props.confirmStyle, padding: "10px 40px", minWidth: 100, display: (props.confirmLabel !== null ? "inline-block" : "none") },
            loadingProps: {
                disabled: true,
                leftIcon: "fas fa-spinner fa-pulse"
            }
        },
    };
    const alert = alertComponent({
        confirm,
        footerElements: props.footerElements,
        message: (<React.Fragment>
            <div className="cd-c" style={props.rightContent && !props.rightStyle ? { flex: 0.8 } : undefined}>
                <span className="title">{props.title}</span>
                {props.desc && (<span className="desc" dangerouslySetInnerHTML={{ __html: props.desc || "" }}></span>)}
                {props.centerIcon ? <div className="ci">{props.centerIcon as any}</div> : null}
                <div className="content" style={{ height: "100%", flex: 1, display: "flex", overflow: "auto" }}>{props.content as any}</div>
            </div>
            {props.rightContent ? (<div className="cd-c-r" style={props.rightStyle}> {props.rightContent as any} </div>) : null}
        </React.Fragment>),
        cancel,
        className: "cd",
        style: props.style,
        ...buildDialogProps(),
        onLoading: props.onLoading,
        alignFooter: footerAlignment,
        componentProps: {
            maximized: props.maximized,
            contentProps: {
                style: props.contentStyle
            }
        },
        dismissibleModal: props.dismissibleModal ?? false,
    });
    const ogUpdate = alert.update.bind(alert);
    alert.update = ((newProps: Partial<ConfirmProps>, onMount?: Function, onUnMount?: Function) => {
        if (cancel && newProps.cancelLabel) cancel.label = newProps.cancelLabel;
        if (confirm && newProps.confirmLabel) confirm.label = newProps.confirmLabel;
        props.reverseControlActions = newProps.reverseControlActions;
        if (props.onLoading && !(newProps as any).__pass_through__) {
            ogUpdate(newProps as any, onMount, onUnMount);
            return;
        }
        ogUpdate({
            confirm,
            style: newProps.style,
            ...buildDialogProps(),
            message: (<React.Fragment>
                <div className="cd-c">
                    <span className="title">{newProps.title ?? props.title}</span>
                    <span className="desc" dangerouslySetInnerHTML={{ __html: newProps.desc ?? props.desc ?? "" }}></span>
                    {(newProps.centerIcon || props.centerIcon) ? <div className="ci">{newProps.centerIcon ?? props.centerIcon as any}</div> : null}
                    <div className="content" style={{ height: "100%", overflow: "auto" }}>{newProps.content ?? props.content as any}</div>
                </div>
                {(newProps.rightContent || props.rightContent) ? (<div className="cd-c-r" style={newProps.rightStyle ?? props.rightStyle}> {newProps.rightContent ?? props.rightContent as any} </div>) : null}
            </React.Fragment>),
        }, onMount, onUnMount);
    }) as any;
    return alert as any;

    function buildDialogProps() {
        return {
            onConfirm: async () => {
                if (props.reverseControlActions) {
                    props.onCancel?.();
                    alert.hide();
                    return;
                }
                if (!props.onConfirm || (props.onConfirm && await props.onConfirm(confirmButton.current))) {
                    alert.hide();
                }
            },
            onCancel: async () => {
                if (props.reverseControlActions) {
                    if (!props.onConfirm || (props.onConfirm && await props.onConfirm(confirmButton.current))) {
                        alert.hide();
                    }
                    return;
                }
                alert.hide();
            },
        };
    }
}

export const Siren = {

    alert: (message: string, options?: Partial<MessageProps>) => {
        Toaster.toast({
            lifetime: 5000,
            foreScheme: true,
            content: message,
            showProgressbar: true,
            pauseDelayOnHover: true,
            scheme: Schemes.OMNEHOST,
            style: { background: "white" },
            icon: MessageSchemesIcons.SUCCESS,
            ...options
        });
    },

    pop: (content: string, title?: string, options?: Partial<ConfirmProps>) => {
        ConfirmDialog({
            title,
            content,
            confirmScheme: Scheme.DANGER,
            ...options,
        }).show();
    },

}
